import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Radio, Play, Pause, Volume2, VolumeX, Users, Music, Share2, Clock, Download, ChevronDown, Copy, Check
} from "lucide-react";
import { BACKEND_URL } from "../context/AuthContext";
import audioBus from "../services/AudioBus";
import { useStreamResilience } from "../hooks/useStreamResilience";
import SEO from "../components/SEO";

const PublicStationPage = () => {
  const { stationId } = useParams();
  const [stationData, setStationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.3);
  const [isMuted, setIsMuted] = useState(false);
  const [analyzerData, setAnalyzerData] = useState(new Array(32).fill(5));
  const [songHistory, setSongHistory] = useState([]);
  const [mounts, setMounts] = useState([]);
  const [selectedMount, setSelectedMount] = useState(null);
  const [showMountDropdown, setShowMountDropdown] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [copied, setCopied] = useState(false);
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const animationRef = useRef(null);
  const audioBusHandle = useRef(null);
  const { isBuffering } = useStreamResilience(audioRef);

  // Fetch station data
  const fetchStation = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/station/${stationId}`);
      if (res.ok) {
        const data = await res.json();
        setStationData(data);
        const stationMounts = data.mounts || [];
        if (stationMounts.length > 0) {
          setMounts(stationMounts);
          setSelectedMount(prev => prev || stationMounts[0]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch station:', err);
    } finally {
      setLoading(false);
    }
  }, [stationId]);

  useEffect(() => {
    fetchStation();
    const interval = setInterval(fetchStation, 15000);
    return () => {
      clearInterval(interval);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (audioBusHandle.current) audioBusHandle.current.unregister();
    };
  }, [fetchStation]);

  // Fetch song history
  useEffect(() => {
    if (!stationId) return;
    const fetchHistory = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/station/${stationId}/history`);
        if (res.ok) {
          const data = await res.json();
          setSongHistory(data.history || []);
        }
      } catch (err) {
        console.error('Failed to fetch song history:', err);
      }
    };
    fetchHistory();
    const historyInterval = setInterval(fetchHistory, 30000);
    return () => clearInterval(historyInterval);
  }, [stationId]);

  // Setup audio analyzer for FFT visualizer
  const setupAnalyzer = useCallback(() => {
    if (!audioRef.current || audioContextRef.current) return;
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyzer = audioContext.createAnalyser();
      const source = audioContext.createMediaElementSource(audioRef.current);
      source.connect(analyzer);
      analyzer.connect(audioContext.destination);
      analyzer.fftSize = 64;
      audioContextRef.current = audioContext;
      const bufferLength = analyzer.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      const animate = () => {
        analyzer.getByteFrequencyData(dataArray);
        const normalized = Array.from(dataArray).map(v => Math.max(5, (v / 255) * 100));
        setAnalyzerData(normalized);
        animationRef.current = requestAnimationFrame(animate);
      };
      animate();
    } catch (err) {
      console.error('Failed to setup audio analyzer:', err);
    }
  }, []);

  const togglePlay = async () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      if (audioContextRef.current?.state === 'suspended') audioContextRef.current.resume();
      if (!audioBusHandle.current) {
        audioBusHandle.current = audioBus.register('public-station', {
          pause: () => { if (audioRef.current) audioRef.current.pause(); setIsPlaying(false); }
        });
      }
      audioBusHandle.current.claim();
      await audioRef.current.play();
      setupAnalyzer();
    }
    setIsPlaying(!isPlaying);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      audioRef.current.muted = false;
    }
  };

  const handleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !audioRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const switchMount = (mount) => {
    setSelectedMount(mount);
    setShowMountDropdown(false);
    const wasPlaying = isPlaying;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = mount.url || stationData?.stream_url;
      audioRef.current.load();
      if (wasPlaying) {
        if (audioBusHandle.current) audioBusHandle.current.claim();
        audioRef.current.play().catch(() => {});
      }
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(typeof ts === 'number' ? ts * 1000 : ts);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Reliable share function
  const handleShare = async () => {
    const shareUrl = window.location.href;
    // Try native Web Share API first (mobile-friendly)
    if (navigator.share) {
      try {
        await navigator.share({ title: stationData?.station_name || 'Listen Live', url: shareUrl });
        return;
      } catch { /* user cancelled or not supported */ }
    }
    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Last resort: use a hidden input
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center">
        <Radio className="w-12 h-12 text-purple-400 animate-pulse" />
      </div>
    );
  }

  if (!stationData) {
    return (
      <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center">
        <div className="text-center">
          <Radio className="w-16 h-16 text-white/30 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Station Not Found</h1>
          <p className="text-white/50">This station doesn't exist or hasn't been set up yet.</p>
        </div>
      </div>
    );
  }

  const primaryColor = stationData.primary_color || '#a855f7';
  const song = stationData.now_playing?.song || {};
  const streamUrl = selectedMount?.url || stationData.stream_url;
  const hlsUrl = stationData.hls_url;
  const showFft = stationData.show_fft !== false;
  const showListeners = stationData.show_listeners !== false;
  const showHistoryBtn = stationData.show_history !== false;
  const cardColor = stationData.player_card_color || '#1a1a3e';
  const currentMountLabel = selectedMount
    ? `${(selectedMount.autodj_format || selectedMount.format || 'MP3').toUpperCase()} ${selectedMount.autodj_bitrate || selectedMount.bitrate || ''}kbps`
    : (hlsUrl ? 'HLS' : 'Stream');

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <SEO
        title={stationData.station_name || "Listen Live"}
        description={`Listen to ${stationData.station_name || 'this station'} live on DJsandMCMedia. ${song.title ? `Now playing: ${song.title} by ${song.artist || 'Unknown'}.` : ''} Stream music 24/7 — real radio, not playlists.`}
        image={song.art || stationData.logo_url || "/og-image.png"}
        type="music.radio_station"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "RadioStation",
          "name": stationData.station_name,
          "url": window.location.href,
          "image": song.art || stationData.logo_url || undefined,
          "description": stationData.description || `Live radio station on DJsandMCMedia`,
          "broadcastFrequency": "Internet",
          "broadcaster": { "@type": "Organization", "name": "DJsandMCMedia", "url": "https://djsandmcmedia.com" },
          ...(song.title ? { "track": { "@type": "MusicRecording", "name": song.title, "byArtist": song.artist || "Unknown" } } : {}),
        }}
      />

      {/* Background — uses station background image if set, otherwise solid dark */}
      {stationData.background_url ? (
        <>
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${stationData.background_url})` }} />
          <div className="absolute inset-0 bg-black/60" />
        </>
      ) : (
        <div className="absolute inset-0 bg-[#0d0d1e]" />
      )}

      {/* Hidden Audio */}
      <audio ref={audioRef} src={streamUrl} crossOrigin="anonymous" preload="none"
        onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} />

      {/* Close mount dropdown when clicking outside */}
      {showMountDropdown && (
        <div className="fixed inset-0 z-20" onClick={() => setShowMountDropdown(false)} />
      )}

      {/* ============ PLAYER CARD ============ */}
      <div className="relative w-full max-w-lg z-10">
        <div className="relative backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl overflow-hidden"
          style={{ backgroundColor: cardColor + 'e6' }}
          data-testid="station-player-card">

          {/* Station Name Header */}
          <div className="px-6 pt-6 pb-4 text-center">
            {stationData.logo_url && (
              <img src={stationData.logo_url} alt={stationData.station_name}
                className="w-14 h-14 rounded-xl mx-auto mb-3 object-cover"
                data-testid="station-logo" />
            )}
            <h1 className="text-xl font-bold" style={{ color: primaryColor }} data-testid="station-name">
              {stationData.station_name}
            </h1>
            {stationData.tagline && <p className="text-white/50 text-xs mt-1">{stationData.tagline}</p>}
          </div>

          {/* Now Playing Row */}
          <div className="px-6 pb-4">
            <div className="flex items-center gap-4" data-testid="now-playing-row">
              {song.art ? (
                <img src={song.art} alt="" className="w-20 h-20 rounded-xl object-cover shadow-lg flex-shrink-0 border border-white/10" data-testid="album-art" />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0 border border-white/10">
                  <Music className="w-8 h-8 text-white/20" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-base truncate" data-testid="track-title">
                  {song.title || 'Unknown Track'}
                </p>
                <p className="text-white/60 text-sm truncate" data-testid="track-artist">
                  {song.artist || 'Unknown Artist'}
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded" style={{ color: primaryColor, backgroundColor: primaryColor + '15' }}>LIVE</span>
                  <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{
                      width: isPlaying ? '100%' : '0%',
                      backgroundColor: primaryColor,
                      transition: 'width 0.8s ease',
                      animation: isPlaying ? 'none' : 'none',
                    }} />
                  </div>
                  {showListeners && (
                    <div className="flex items-center gap-1 text-[10px] text-white/30">
                      <Users className="w-3 h-3" />
                      <span data-testid="listener-count">{stationData.now_playing?.listeners || 0}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Controls Row */}
          <div className="px-6 pb-5">
            <div className="flex items-center gap-3" data-testid="controls-row">
              <button onClick={togglePlay}
                className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-105 flex-shrink-0"
                style={{ backgroundColor: isPlaying ? primaryColor : '#ffffff' }}
                data-testid="play-pause-btn">
                {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-black ml-0.5" />}
              </button>

              {/* Mount/Quality Selector */}
              <div className="relative">
                <button onClick={() => setShowMountDropdown(!showMountDropdown)}
                  className="flex items-center gap-1.5 bg-white/10 hover:bg-white/15 text-white/80 px-3 py-2 rounded-lg text-xs font-medium transition"
                  data-testid="mount-selector-btn">
                  <span>{currentMountLabel}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                {showMountDropdown && (
                  <div className="absolute top-full left-0 mt-1 bg-[#1e1e45] border border-white/10 rounded-xl shadow-2xl z-30 min-w-[160px] py-1 overflow-hidden"
                    data-testid="mount-dropdown">
                    {mounts.map((m, idx) => (
                      <button key={m.id || idx}
                        onClick={() => switchMount(m)}
                        className={`w-full text-left px-4 py-2.5 text-sm transition flex items-center justify-between ${
                          selectedMount?.id === m.id ? 'bg-white/10 text-white' : 'text-white/60 hover:bg-white/5 hover:text-white'
                        }`}
                        data-testid={`mount-option-${idx}`}>
                        <span>{(m.autodj_format || m.format || 'mp3').toUpperCase()}</span>
                        <span className="text-white/30 text-xs">{m.autodj_bitrate || m.bitrate || '?'}kbps</span>
                      </button>
                    ))}
                    {hlsUrl && (
                      <button onClick={() => switchMount({ url: hlsUrl, format: 'HLS', bitrate: 'Adaptive' })}
                        className="w-full text-left px-4 py-2.5 text-sm text-white/60 hover:bg-white/5 hover:text-white transition flex items-center justify-between"
                        data-testid="mount-option-hls">
                        <span>HLS</span>
                        <span className="text-white/30 text-xs">Adaptive</span>
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Volume */}
              <div className="flex items-center gap-2 flex-1 ml-auto">
                <button onClick={handleMute} className="text-white/40 hover:text-white transition" data-testid="mute-btn">
                  {isMuted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
                <input type="range" min="0" max="1" step="0.01" value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer
                    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5
                    [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer"
                  style={{ background: `linear-gradient(to right, rgba(255,255,255,0.6) ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.1) ${(isMuted ? 0 : volume) * 100}%)` }}
                  data-testid="volume-slider" />
              </div>
            </div>
          </div>

          {/* Bottom Row: Song History + Share + Playlist Downloads */}
          <div className="border-t border-white/5 flex items-center divide-x divide-white/5">
            {showHistoryBtn && (
              <button onClick={() => setShowHistory(!showHistory)}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 text-white/50 hover:text-white hover:bg-white/5 transition text-sm font-medium"
                data-testid="song-history-toggle">
                <Clock className="w-4 h-4" /> HISTORY
              </button>
            )}
            <button onClick={handleShare}
              className="flex items-center justify-center gap-2 px-5 py-3.5 text-white/50 hover:text-white hover:bg-white/5 transition text-sm font-medium"
              data-testid="share-btn">
              {copied ? <><Check className="w-4 h-4 text-green-400" /> <span className="text-green-400">Copied!</span></> : <><Share2 className="w-4 h-4" /> SHARE</>}
            </button>
            <div className="flex-1 flex items-center justify-center gap-3 py-3.5">
              <a href={`${BACKEND_URL}/api/listen/${stationId}/playlist/pls`} download={`${stationId}.pls`}
                className="flex items-center gap-1.5 text-white/50 hover:text-white transition text-sm font-medium"
                data-testid="download-pls">
                <Download className="w-4 h-4" /> PLS
              </a>
              <span className="text-white/10">|</span>
              <a href={`${BACKEND_URL}/api/listen/${stationId}/playlist/m3u`} download={`${stationId}.m3u`}
                className="flex items-center gap-1.5 text-white/50 hover:text-white transition text-sm font-medium"
                data-testid="download-m3u">
                <Download className="w-4 h-4" /> M3U
              </a>
            </div>
          </div>

          {/* Song History Panel */}
          {showHistory && songHistory.length > 0 && (
            <div className="border-t border-white/5 px-5 py-4" data-testid="song-history-panel">
              <div className="space-y-1.5 max-h-[240px] overflow-y-auto pr-1">
                {songHistory.slice(0, 8).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-white/[0.03] rounded-xl p-2.5 hover:bg-white/[0.06] transition"
                    data-testid={`history-item-${idx}`}>
                    {item.art ? (
                      <img src={item.art} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                        <Music className="w-5 h-5 text-white/15" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{item.title || 'Unknown'}</p>
                      <p className="text-white/40 text-xs truncate">{item.artist || 'Unknown'}</p>
                    </div>
                    {item.played_at && (
                      <span className="text-white/25 text-xs flex-shrink-0">{formatTime(item.played_at)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ============ FFT VISUALIZER UNDER THE PLAYER ============ */}
        {showFft && (
          <div className="mt-4 flex items-end justify-center gap-[3px] h-20 px-4" data-testid="fft-visualizer">
            {analyzerData.slice(0, 32).map((height, i) => (
              <div key={i}
                className="flex-1 rounded-t-sm transition-all duration-75"
                style={{
                  height: `${isPlaying ? Math.max(6, height * 0.8) : 6}%`,
                  backgroundColor: isPlaying ? primaryColor : 'rgba(255,255,255,0.08)',
                  opacity: isPlaying ? (0.5 + (height / 200)) : 0.3,
                }} />
            ))}
          </div>
        )}

        {/* Powered By */}
        <div className="mt-4 text-center">
          <a href="/" className="text-white/20 text-xs hover:text-white/40 transition" data-testid="powered-by">
            Powered by DJsandMCMedia
          </a>
        </div>
      </div>
    </div>
  );
};

export default PublicStationPage;
